import {epos2Axios, goAxios} from "@/interceptors/axios";

class LogsService {
    getFirstDefined(...values) {
        return values.find((value) => value !== null && typeof value !== 'undefined');
    }

    getNestedValue(source, path) {
        return path.split('.').reduce((value, key) => value?.[key], source);
    }

    getFirstNestedValue(source, paths = []) {
        for (const path of paths) {
            const value = this.getNestedValue(source, path);
            if (value !== null && typeof value !== 'undefined') {
                return value;
            }
        }

        return undefined;
    }

    toPriceNumber(value) {
        if (value === null || typeof value === 'undefined' || value === '') {
            return null;
        }

        const parsedValue = Number(value);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    formatPrice(value) {
        const numericValue = this.toPriceNumber(value);
        return numericValue === null ? null : numericValue.toFixed(2);
    }

    normalizeActionLabel(action) {
        return String(action || '').trim().toLowerCase();
    }

    isCreateAction(action) {
        const normalizedAction = this.normalizeActionLabel(action);
        return ['create', 'created', 'add', 'added', 'insert', 'inserted', 'store', 'stored'].some((keyword) => normalizedAction.includes(keyword));
    }

    isDeleteAction(action) {
        const normalizedAction = this.normalizeActionLabel(action);
        return ['delete', 'deleted', 'remove', 'removed', 'destroy', 'destroyed'].some((keyword) => normalizedAction.includes(keyword));
    }

    buildServicePriceDetails(priceColumn, action, oldPrice, newPrice, fallbackDetails) {
        const detailParts = [];

        if (oldPrice !== null) {
            detailParts.push('Old Price: ' + this.formatPrice(oldPrice));
        }

        if (newPrice !== null) {
            detailParts.push('New Price: ' + this.formatPrice(newPrice));
        }

        const formattedDetails = detailParts.length > 0
            ? detailParts.join(', ')
            : (typeof fallbackDetails === 'string' && fallbackDetails.trim() !== '' ? fallbackDetails : 'N/A');

        const detailLabel = priceColumn || 'Price';
        const actionLabel = action ? ' (' + action + ')' : '';

        return detailLabel + ' : ' + formattedDetails + actionLabel;
    }

    getServicePriceChangeDirection(action, oldPrice, newPrice) {
        if (this.isCreateAction(action) || this.isDeleteAction(action)) {
            return 'na';
        }

        if (oldPrice === null || newPrice === null || oldPrice === newPrice) {
            return 'na';
        }

        return newPrice > oldPrice ? 'increase' : 'decrease';
    }

    buildLogConfig(serverOptions = {}) {
        return {
            params: {
                page: serverOptions.page,
                limit: serverOptions.rowsPerPage,
                order_by: serverOptions.sortBy,
                order_direction: serverOptions.sortType,
                filters: serverOptions.filters,
                start_date: serverOptions.start_date,
                end_date: serverOptions.end_date,
            },
        };
    }

    normalizeSortSpecs(serverOptions = {}) {
        const rawSortBy = serverOptions.sortBy;
        const rawSortType = serverOptions.sortType;
        const sortBy = Array.isArray(rawSortBy)
            ? rawSortBy
            : (rawSortBy ? [rawSortBy] : []);
        const sortType = Array.isArray(rawSortType)
            ? rawSortType
            : (rawSortType ? [rawSortType] : []);

        return sortBy.map((field, index) => ({
            field,
            direction: String(sortType[index] || 'asc').toLowerCase(),
        }));
    }

    extractLogRows(data, explicitKeys = []) {
        for (const key of explicitKeys) {
            if (Array.isArray(data?.[key])) {
                return data[key];
            }
        }

        for (const value of Object.values(data || {})) {
            if (Array.isArray(value)) {
                return value;
            }
        }

        return [];
    }

    flattenServicePriceLogs(rows = []) {
        return rows.flatMap((row) => {
            if (Array.isArray(row?.logs)) {
                return row.logs.map((logEntry) => ({
                    ...logEntry,
                    change_set_id: logEntry.change_set_id || row.change_set_id,
                }));
            }

            return [row];
        });
    }

    extractFoundRows(data, rows) {
        if (typeof data?.pagination?.found_rows === 'number') {
            return data.pagination.found_rows;
        }

        if (typeof data?.found_rows === 'number') {
            return data.found_rows;
        }

        return rows.length;
    }

    getLocalSortSpecs(serverOptions = {}) {
        return this.normalizeSortSpecs(serverOptions)
            .filter((spec) => spec.field === 'price_change_direction');
    }

    buildServicePriceLogConfig(serverOptions = {}) {
        const remoteSortSpec = this.normalizeSortSpecs(serverOptions)
            .find((spec) => spec.field !== 'price_change_direction');

        return this.buildLogConfig({
            ...serverOptions,
            sortBy: remoteSortSpec?.field || undefined,
            sortType: remoteSortSpec?.direction || undefined,
        });
    }

    sortServicePriceLogs(rows = [], serverOptions = {}) {
        const localSortSpecs = this.getLocalSortSpecs(serverOptions);

        if (localSortSpecs.length === 0) {
            return rows;
        }

        const directionRank = {
            decrease: 0,
            na: 1,
            increase: 2,
        };

        return [...rows].sort((left, right) => {
            for (const spec of localSortSpecs) {
                const leftRank = directionRank[left?.price_change_direction] ?? directionRank.na;
                const rightRank = directionRank[right?.price_change_direction] ?? directionRank.na;

                if (leftRank === rightRank) {
                    continue;
                }

                return spec.direction === 'desc' ? rightRank - leftRank : leftRank - rightRank;
            }

            return 0;
        });
    }

    normalizeServicePriceLogEntry(entry = {}) {
        const serviceData = entry.servicetype || entry.service || entry.service_price || {};
        const garmentTypeData = entry.garmenttype || entry.garment_type || {};
        const userData = entry.user || {};
        const changeData = entry.changes || entry.change || entry.diff || entry.details || {};
        const publicPriceValue = entry.publicPrice ?? entry.public_price ?? entry.serviceprice_public ?? serviceData.publicPrice ?? serviceData.public_price;
        const oldPrice = this.toPriceNumber(this.getFirstDefined(
            entry.old_price,
            entry.oldPrice,
            entry.price_old,
            entry.previous_price,
            entry.before_price,
            this.getFirstNestedValue(entry, [
                'old.priceband_standard',
                'before.priceband_standard',
                'previous.priceband_standard',
                'service_price_old.priceband_standard',
                'servicePriceOld.priceband_standard',
            ]),
            this.getFirstNestedValue(changeData, [
                'old_price',
                'oldPrice',
                'old.priceband_standard',
                'before.priceband_standard',
                'previous.priceband_standard',
            ])
        ));
        const newPrice = this.toPriceNumber(this.getFirstDefined(
            entry.new_price,
            entry.newPrice,
            entry.price_new,
            entry.current_price,
            entry.after_price,
            this.getFirstNestedValue(entry, [
                'new.priceband_standard',
                'after.priceband_standard',
                'current.priceband_standard',
                'service_price_new.priceband_standard',
                'servicePriceNew.priceband_standard',
            ]),
            this.getFirstNestedValue(changeData, [
                'new_price',
                'newPrice',
                'new.priceband_standard',
                'after.priceband_standard',
                'current.priceband_standard',
            ])
        ));
        const action = entry.action || entry.event || entry.mode || entry.log_type || '';
        const fallbackDetails = typeof entry.details === 'string'
            ? entry.details
            : (typeof entry.info === 'string' ? entry.info : '');
        const changeDirection = this.getFirstDefined(entry.change_direction, entry.changeDirection);

        return {
            logged_at: entry.logged_at || entry.action_timestamp || entry.created_at || entry.updated_at || entry.timestamp || '',
            user_name: entry.user_name || entry.username || userData.username || userData.userlabel || '',
            action,
            service_name: entry.service_name || entry.service_type_name || entry.servicetype || serviceData.servicetype || serviceData.name || '',
            garment_type_name: entry.garment_type_name || entry.garment_name || entry.garmenttype || garmentTypeData.garment_type || garmentTypeData.garmenttype || '',
            garment_type_id: entry.garment_type_id || entry.garmenttype_id || garmentTypeData.garmenttype_id || garmentTypeData.garment_type_id || '',
            service_price_id: entry.service_price_id || entry.serviceprice_id || entry.serviceid || serviceData.serviceprice_id || serviceData.serviceid || '',
            public_price: publicPriceValue === 1 || publicPriceValue === '1' || publicPriceValue === true ? 'Yes' : 'No',
            details: this.buildServicePriceDetails(entry.price_column, action, oldPrice, newPrice, fallbackDetails),
            price_change_direction: changeDirection === 1 ? 'increase' : (changeDirection === -1 ? 'decrease' : this.getServicePriceChangeDirection(action, oldPrice, newPrice)),
            old_price: oldPrice,
            new_price: newPrice,
            raw_details: JSON.stringify(entry, null, 2),
        };
    }

    async searchDriverPerformanceLogs(serverOptions) {
        try {
            let config = {
                params: {
                    page: serverOptions.page,
                    limit: serverOptions.rowsPerPage,
                    order_by: serverOptions.sortBy,
                    order_direction: serverOptions.sortType,
                    filters: serverOptions.filters,
                },
            }

            config.params.start_date = serverOptions.start_date;
            config.params.end_date = serverOptions.end_date;

            const response = await epos2Axios.get('/api/logs/driver-performance', config);

            return {
                serverCurrentPageItems: response.data.data.driver_performance,
                serverTotalItemsLength: response.data.data.pagination.found_rows,
            };
        } catch (error) {
            console.error('Error fetching data:', error);
            return {
                serverCurrentPageItems: [],
                serverTotalItemsLength: 0,
            };
        }
    }


    async downloadDriverPerformanceLogs(serverOptions) {
        try {
            let  params = {
                order_by: serverOptions.sortBy,
                order_direction: serverOptions.sortType,
                filters: serverOptions.filters,
                export: 'csv',
            }
            if(serverOptions.start_date !== ""){
                params.start_date = serverOptions.start_date;
            }
            if(serverOptions.end_date !== ""){
                params.end_date = serverOptions.end_date;
            }
            const response = await epos2Axios.get('/api/logs/driver-performance', {
                params,
                responseType: 'blob', // Set the response type to 'blob' for binary data
            });

            const blob = new Blob([response.data], { type: 'application/zip' });

            // We create a temporary link that contains the zip file
            // we then click it programmatically to download the file
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'driver_performance.zip';
            document.body.appendChild(link);
            link.click();

            // Remove the link from the document
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    }



    async searchShoeRepairLogs(serverOptions) {
        try {
            let config = {
                params: {
                    page: serverOptions.page,
                    limit: serverOptions.rowsPerPage,
                    order_by: serverOptions.sortBy,
                    order_direction: serverOptions.sortType,
                    filters: serverOptions.filters,
                },
            }

            config.params.start_date = serverOptions.start_date;
            config.params.end_date = serverOptions.end_date;

            const response = await epos2Axios.get('/api/logs/shoe-repair', config);

            return {
                serverCurrentPageItems: response.data.data.shoe_repair,
                serverTotalItemsLength: response.data.data.pagination.found_rows,
            };
        } catch (error) {
            console.error('Error fetching data:', error);
            return {
                serverCurrentPageItems: [],
                serverTotalItemsLength: 0,
            };
        }
    }


    async downloadShoeRepairLogs(serverOptions) {
        try {
            let  params = {
                order_by: serverOptions.sortBy,
                order_direction: serverOptions.sortType,
                filters: serverOptions.filters,
                export: 'csv',
            }
            if(serverOptions.start_date !== ""){
                params.start_date = serverOptions.start_date;
            }
            if(serverOptions.end_date !== ""){
                params.end_date = serverOptions.end_date;
            }
            const response = await epos2Axios.get('/api/logs/shoe-repair', {
                params,
                responseType: 'blob', // Set the response type to 'blob' for binary data
            });

            const blob = new Blob([response.data], { type: 'application/zip' });

            // We create a temporary link that contains the zip file
            // we then click it programmatically to download the file
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'shoe_repair.zip';
            document.body.appendChild(link);
            link.click();

            // Remove the link from the document
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    }



    async searchLogisticsLogs(serverOptions) {
        console.log(serverOptions)
        try {
            let config = {
                params: {
                    page: serverOptions.page,
                    limit: serverOptions.rowsPerPage,
                    order_by: serverOptions.sortBy,
                    order_direction: serverOptions.sortType,
                    filters: serverOptions.filters,
                },
            }

            config.params.start_date = serverOptions.start_date;
            config.params.end_date = serverOptions.end_date;

            const response = await epos2Axios.get('/api/logs/logistics', config);

            return {
                serverCurrentPageItems: response.data.data.logistics,
                serverTotalItemsLength: response.data.data.pagination.found_rows,
            };
        } catch (error) {
            console.error('Error fetching data:', error);
            return {
                serverCurrentPageItems: [],
                serverTotalItemsLength: 0,
            };
        }
    }


    async downloadLogisticsLogs(serverOptions) {
        try {
            let  params = {
                order_by: serverOptions.sortBy,
                order_direction: serverOptions.sortType,
                filters: serverOptions.filters,
                export: 'csv',
            }
            if(serverOptions.start_date !== ""){
                params.start_date = serverOptions.start_date;
            }
            if(serverOptions.end_date !== ""){
                params.end_date = serverOptions.end_date;
            }
            const response = await epos2Axios.get('/api/logs/logistics', {
                params,
                responseType: 'blob', // Set the response type to 'blob' for binary data
            });

            const blob = new Blob([response.data], { type: 'application/zip' });

            // We create a temporary link that contains the zip file
            // we then click it programmatically to download the file
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'logistics_log.zip';
            document.body.appendChild(link);
            link.click();

            // Remove the link from the document
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    }

    async searchServicePriceLogs(garmentTypeId, serverOptions) {
        try {
            const endpoint = garmentTypeId
                ? '/api/serviceprices/' + garmentTypeId + '/logs'
                : '/api/serviceprices/logs';
            const response = await goAxios.get(endpoint, this.buildServicePriceLogConfig(serverOptions));
            const rows = this.extractLogRows(response.data?.data, [
                'service_price_logs',
                'serviceprice_logs',
                'logs',
            ]);
            const flattenedRows = this.flattenServicePriceLogs(rows);
            const normalizedRows = flattenedRows.map((entry) => this.normalizeServicePriceLogEntry(entry));

            return {
                serverCurrentPageItems: this.sortServicePriceLogs(normalizedRows, serverOptions),
                serverTotalItemsLength: this.extractFoundRows(response.data?.data, flattenedRows),
            };
        } catch (error) {
            console.error('Error fetching data:', error);
            return {
                serverCurrentPageItems: [],
                serverTotalItemsLength: 0,
            };
        }
    }
}

export default new LogsService();